import { useStore } from '@nanostores/react';
import { accountStore } from '@/services/auth/store';
import { VerificationReviewState, VerificationState, type RawAccount } from '@/services/auth/types';

/**
 * The two fields every verification decision reads, kept as plain strings so an
 * unrecognised value still registers as a change.
 */
type VerificationFields = {
  verification_state?: string | null;
  verification_review_state?: string | null;
};

export type VerificationSnapshot = {
  state: string | undefined;
  reviewState: string | undefined;
};

export function toVerificationSnapshot(account: VerificationFields | null | undefined): VerificationSnapshot {
  return {
    state: account?.verification_state ?? undefined,
    reviewState: account?.verification_review_state ?? undefined,
  };
}

/** Approved or rejected: the review is over and nothing further will arrive. */
export function isVerificationTerminal(snapshot: VerificationSnapshot): boolean {
  return snapshot.state === VerificationState.Approved || snapshot.state === VerificationState.Rejected;
}

export function isAwaitingMoreInfo(snapshot: VerificationSnapshot): boolean {
  return snapshot.reviewState === VerificationReviewState.NeedsMoreInfo;
}

/** Both fields, because `review` -> `needs_more_info` moves without `verification_state` changing. */
export function hasVerificationChanged(previous: VerificationSnapshot, next: VerificationSnapshot): boolean {
  return previous.state !== next.state || previous.reviewState !== next.reviewState;
}

/**
 * Whether the account passed the anti-abuse verification review. Single swap
 * point: the checklist, the `/onboarding` bounce, and the go-live gate all
 * read it. Takes the account so the pre-auth signup landing can use it too.
 */
export function isAccountVerified(account: Pick<RawAccount, 'verification_state'> | null | undefined): boolean {
  return account?.verification_state === VerificationState.Approved;
}

/** Store-backed reader, for use inside the authenticated tree. */
export function useIsAccountVerified(): boolean {
  return isAccountVerified(useStore(accountStore));
}

/**
 * Whether the review is waiting on the customer. Rejection is deliberately not
 * included: a rejected account gets the takeover instead of the checklist, so
 * there is no step for it to act on.
 */
export function useVerificationNeedsAction(): boolean {
  return useStore(accountStore)?.verification_review_state === VerificationReviewState.NeedsMoreInfo;
}

export function useIsAccountRejected(): boolean {
  return useStore(accountStore)?.verification_state === VerificationState.Rejected;
}
